import type { CrateItem, MissingCrateItem } from '@/entities/order/model/order.types'

const ZEBRA_SCAN_STORAGE_KEY = 'helloScreens:zebraScanState'

export interface ZebraOrderScanState {
  scannedBarcodes: CrateItem[]
  missingCrates: MissingCrateItem[]
  expectedBarcodes?: string[]
  expectedCrateItems?: CrateItem[]
  updatedAt: string
}

interface ZebraScanStorageSchema {
  version: 1
  zebraScanState: Record<string, ZebraOrderScanState>
}

function createDefaultStorageState(): ZebraScanStorageSchema {
  return {
    version: 1,
    zebraScanState: {},
  }
}

function isBrowser() {
  return typeof window !== 'undefined' && !!window.localStorage
}

function readStorage(): ZebraScanStorageSchema {
  if (!isBrowser()) {
    return createDefaultStorageState()
  }

  try {
    const raw = window.localStorage.getItem(ZEBRA_SCAN_STORAGE_KEY)
    if (!raw) {
      return createDefaultStorageState()
    }
    const parsed = JSON.parse(raw) as Partial<ZebraScanStorageSchema>
    if (parsed.version !== 1 || typeof parsed.zebraScanState !== 'object' || parsed.zebraScanState === null) {
      return createDefaultStorageState()
    }
    return {
      version: 1,
      zebraScanState: parsed.zebraScanState,
    }
  } catch {
    return createDefaultStorageState()
  }
}

function writeStorage(nextState: ZebraScanStorageSchema) {
  if (!isBrowser()) {
    return
  }
  window.localStorage.setItem(ZEBRA_SCAN_STORAGE_KEY, JSON.stringify(nextState))
}

/** Every order's persisted scan state, read in one parse. For passes that touch many orders. */
export function readAllZebraOrderScanStates(): Record<string, ZebraOrderScanState> {
  return readStorage().zebraScanState
}

/** Replace every order's persisted scan state in one write. Pairs with readAllZebraOrderScanStates. */
export function writeAllZebraOrderScanStates(zebraScanState: Record<string, ZebraOrderScanState>) {
  writeStorage({ version: 1, zebraScanState })
}

export function getZebraOrderScanState(orderId: string): ZebraOrderScanState | null {
  const state = readStorage()
  return state.zebraScanState[orderId] ?? null
}

export function upsertZebraOrderScanState(
  orderId: string,
  scanState: Pick<ZebraOrderScanState, 'scannedBarcodes' | 'missingCrates'>,
) {
  const state = readStorage()
  const existing = state.zebraScanState[orderId]
  state.zebraScanState[orderId] = {
    scannedBarcodes: scanState.scannedBarcodes,
    missingCrates: scanState.missingCrates,
    expectedBarcodes: existing?.expectedBarcodes ?? [],
    expectedCrateItems: existing?.expectedCrateItems ?? [],
    updatedAt: new Date().toISOString(),
  }
  writeStorage(state)
}

export function upsertZebraOrderExpectedBarcodes(orderId: string, expectedBarcodes: string[]) {
  const state = readStorage()
  const existing = state.zebraScanState[orderId]
  state.zebraScanState[orderId] = {
    scannedBarcodes: existing?.scannedBarcodes ?? [],
    missingCrates: existing?.missingCrates ?? [],
    expectedBarcodes,
    expectedCrateItems: existing?.expectedCrateItems ?? [],
    updatedAt: new Date().toISOString(),
  }
  writeStorage(state)
}

export function upsertZebraOrderExpectedCrateItems(orderId: string, expectedCrateItems: CrateItem[]) {
  const state = readStorage()
  const existing = state.zebraScanState[orderId]
  state.zebraScanState[orderId] = {
    scannedBarcodes: existing?.scannedBarcodes ?? [],
    missingCrates: existing?.missingCrates ?? [],
    expectedBarcodes: expectedCrateItems.map(item => item.barcode),
    expectedCrateItems,
    updatedAt: new Date().toISOString(),
  }
  writeStorage(state)
}

export function clearZebraOrderScanState(orderId: string) {
  const state = readStorage()
  if (!state.zebraScanState[orderId]) {
    return
  }
  delete state.zebraScanState[orderId]
  writeStorage(state)
}

/** Order IDs that currently have persisted Zebra scan / missing state (used for offline reconnect reconciliation). */
export function listOrderIdsWithZebraScanState(): string[] {
  const state = readStorage()
  return Object.keys(state.zebraScanState)
}
